import React, { useEffect, useState } from 'react';
import { RelayTrafficLog, getRelayTrafficLogs } from '../api/admin';
import styles from './CommonPage.module.css';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';

const RelayTrafficLogPage: React.FC = () => {
  const [logs, setLogs] = useState<RelayTrafficLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [username, setUsername] = useState('');

  const fetchLogs = async (pageNumber: number) => {
    try {
      setLoading(true);
      const response = await getRelayTrafficLogs(pageNumber, 20, username);
      setLogs(response.content);
      setTotalPages(response.totalPages);
      setPage(pageNumber);
    } catch (error) {
      console.error('Failed to fetch relay traffic logs', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(0);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    fetchLogs(0);
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}시간`);
    if (minutes > 0) parts.push(`${minutes}분`);
    parts.push(`${remainingSeconds}초`);
    
    return parts.join(' ');
  };

  const handlePrev = () => {
    if (page > 0) fetchLogs(page - 1);
  };

  const handleNext = () => {
    if (page < totalPages - 1) fetchLogs(page + 1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.wrapper}>
            <h1>Relay 트래픽 로그</h1>
            <p>Relay 서버를 통한 원격 제어 및 파일 전송 트래픽 이력을 조회합니다.</p>
        </div>
      </div>
      
      <div className={styles.card} style={{ marginBottom: '24px' }}>
          <div className={styles.cardAboveBox} style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', flex: 1 }}>
                <div className={styles.inputBox} style={{ position: 'relative', width: '300px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                    <input 
                        style={{ width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }}
                        placeholder="사용자 ID 검색..."
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                    />
                </div>
                <button type="submit" className={styles.primaryBtn}>검색</button>
            </form>
          </div>
      </div>

      <div className={styles.tableContainer}>
        {loading ? (
          <div style={{ padding: '24px', textAlign: 'center' }}>로딩 중...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>기록 일시</th>
                <th>사용자</th>
                <th>세션 ID</th>
                <th>유형</th>
                <th>전체 사용량</th>
                <th>보낸 데이터</th>
                <th>받은 데이터</th>
                <th>지속 시간</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, index) => (
                <tr key={index}>
                  <td data-label="기록 일시">{new Date(log.createdAt).toLocaleString('ko-KR')}</td>
                  <td data-label="사용자">{log.username}</td>
                  <td data-label="세션 ID">{log.sessionId}</td>
                  <td data-label="유형">
                    <span style={{ 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      backgroundColor: log.connType === 'FILE_TRANSFER' ? '#e0f2fe' : '#f3f4f6',
                      color: log.connType === 'FILE_TRANSFER' ? '#0369a1' : '#374151'
                    }}>
                      {log.connType === 'FILE_TRANSFER' ? '파일 전송' : '원격 제어'}
                    </span>
                  </td>
                  <td data-label="전체 사용량" style={{ fontWeight: 'bold' }}>{formatBytes(log.bytesSent + log.bytesReceived)}</td>
                  <td data-label="보낸 데이터" style={{ color: '#6b7280' }}>{formatBytes(log.bytesSent)}</td>
                  <td data-label="받은 데이터" style={{ color: '#6b7280' }}>{formatBytes(log.bytesReceived)}</td>
                  <td data-label="지속 시간">{formatDuration(log.durationSeconds)}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>데이터가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.pagination}>
          <button onClick={handlePrev} disabled={page === 0}>
              <ChevronLeft size={16} /> 이전
          </button>
          <span>
              {page + 1} / {totalPages === 0 ? 1 : totalPages} 페이지
          </span>
          <button onClick={handleNext} disabled={page >= totalPages - 1}>
              다음 <ChevronRight size={16} />
          </button>
      </div>
    </div>
  );
};

export default RelayTrafficLogPage;
